import React from 'react';
import { Form, Radio, Checkbox, Typography, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTranslatedOptionLabel } from '../../utils/lifestyleCategoriesI18n';

const { Title, Text } = Typography;

const LifestyleCategoryFormI18n = ({ category, formName }) => {
  const { t } = useTranslation();

  const renderFormItem = (param, index) => {
    // Skip Header types - they're just for display
    if (param.type === 'Header') {
      return (
        <div key={`${param.key}-${index}`} className="lifestyle-header">
          <Divider />
          <Title level={5}>{param.name}</Title>
          <Text type="secondary">{param.questionText}</Text>
        </div>
      );
    }

    // Handle BooleanCheckbox types
    if (param.type === 'BooleanCheckbox') {
      // Check if this is a multi-option checkbox question
      if (param.options && param.options.length > 0) {
        return (
          <div key={`${param.key}-${index}`} className="lifestyle-checkbox-group">
            <div className="checkbox-group-label">
              <Text strong>{param.questionText}</Text>
            </div>
            <div className="checkbox-options">
              {param.options.map((option, optIndex) => {
                const fieldName = [formName, param.key, option.key];
                return (
                  <Form.Item
                    key={`${param.key}_${option.key}_${optIndex}`}
                    name={fieldName}
                    valuePropName="checked"
                    className="lifestyle-checkbox-item"
                  >
                    <Checkbox>
                      <Text>{getTranslatedOptionLabel(option, t)}</Text>
                    </Checkbox>
                  </Form.Item>
                );
              })}
            </div>
          </div>
        );
      } else {
        // Single checkbox
        return (
          <Form.Item
            key={`${param.key}-${index}`}
            name={[formName, param.key]}
            valuePropName="checked"
            className="lifestyle-checkbox-item"
          >
            <Checkbox>
              <Text>{param.questionText}</Text>
            </Checkbox>
          </Form.Item>
        );
      }
    }

    // Handle Questionnaire types (radio buttons as per user preference)
    if (param.type === 'Questionnaire') {
      // Special handling for fruit_intake and nature_time_days_weekly
      const isSpecialParameter = param.key === 'fruit_intake' || param.key === 'nature_time_days_weekly';

      let inputNode;
      if (param.options) {
        inputNode = (
          <Radio.Group className="lifestyle-radio-group">
            {param.options.map(option => {
              // For special parameters, use direct label or value if translation fails
              let optionLabel = getTranslatedOptionLabel(option, t);
              if (isSpecialParameter && (!optionLabel || optionLabel.includes('lifestyle.options'))) {
                optionLabel = option.label || option.value;
              }

              return (
                <Radio key={option.value} value={option.value} className="lifestyle-radio-option">
                  {optionLabel}
                </Radio>
              );
            })}
          </Radio.Group>
        );
      } else {
        // Fallback if somehow a Questionnaire has no options
        inputNode = <Text type="secondary">Input type not defined for this parameter.</Text>;
      }

      // For special parameters, use direct name if translation fails
      let questionText = param.questionText;
      if (isSpecialParameter && (!questionText || questionText.includes('lifestyle.questions'))) {
        questionText = param.name;
      }

      return (
        <Form.Item
          key={`${param.key}-${index}`}
          name={[formName, param.key]}
          label={<Text strong>{questionText} <span style={{ color: '#ff4d4f' }}>*</span></Text>}
          className="lifestyle-form-item"
          rules={[
            {
              required: true,
              message: t('healthSurvey.validation.lifestyleQuestionRequired')
            }
          ]}
        >
          {inputNode}
        </Form.Item>
      );
    }

    // Fallback for unexpected types
    return <Text key={`${param.key}-${index}`} type="secondary">Unsupported parameter type: {param.type}</Text>;
  };

  return (
    <div className="lifestyle-category-form">
      <div className="category-description">
        <Text type="secondary">
          {t('lifestyle.headers.categoryDescription', { 
            category: category.title.toLowerCase(),
            defaultValue: `Please answer all questions in this section. Your responses help us understand your ${category.title.toLowerCase()} habits.`
          })}
        </Text>
      </div>

      <div className="lifestyle-parameters">
        {category.parameters.map((param, index) => 
          renderFormItem(param, index)
        )}
      </div>
    </div>
  );
};

export default LifestyleCategoryFormI18n;
